import type {
  KnowledgeDocument,
  KnowledgeValidationResult,
  ProposedDraftChange,
} from '../../api';

export interface KnowledgeReviewReadiness {
  ready: boolean;
  blockers: string[];
  checklistIssues: Array<{
    area: string;
    requirement: string;
    status: 'Missing' | 'Inconsistent';
    nextQuestion?: string;
  }>;
}

export function buildKnowledgeReviewReadiness(options: {
  documents: KnowledgeDocument[];
  dirtyPaths: string[];
  proposedChanges: ProposedDraftChange[];
  validation?: KnowledgeValidationResult;
}): KnowledgeReviewReadiness {
  const blockers: string[] = [];
  const pendingChanges = options.proposedChanges.filter(
    change => change.status === 'pending',
  );
  const checklistIssues = extractChecklistIssues(options.documents);

  if (!options.validation) {
    blockers.push('Run validation after the latest document changes.');
  } else if (!options.validation.valid) {
    blockers.push(
      `Resolve ${options.validation.errors.length} validation error(s).`,
    );
  }
  if (pendingChanges.length) {
    blockers.push(
      `Approve or reject ${pendingChanges.length} pending AI change(s).`,
    );
  }
  if (options.dirtyPaths.length) {
    blockers.push(`Save ${options.dirtyPaths.length} modified document(s).`);
  }
  if (checklistIssues.length) {
    blockers.push(
      `Resolve or explicitly mark not applicable ${checklistIssues.length} completeness item(s).`,
    );
  }

  return { ready: blockers.length === 0, blockers, checklistIssues };
}

export function extractChecklistIssues(documents: KnowledgeDocument[]) {
  const checklist = documents.find(document =>
    /(^|\/)completeness-checklist\.md$/i.test(document.path),
  );
  if (!checklist) {
    return [];
  }

  return checklist.content
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.startsWith('|') && line.endsWith('|'))
    .map(line =>
      line
        .split('|')
        .slice(1, -1)
        .map(cell => cell.trim()),
    )
    .filter(cells => cells[2] === 'Missing' || cells[2] === 'Inconsistent')
    .map(cells => ({
      area: cells[0] || 'General',
      requirement: cells[1] || 'Unspecified requirement',
      status: cells[2] as 'Missing' | 'Inconsistent',
      nextQuestion: cells[4] || undefined,
    }));
}

// Rewrites the Status cell of the first row matching area+requirement to
// 'Not applicable', leaving every other row untouched. Used to let the
// review panel resolve a completeness item without hand-editing the table.
export function markChecklistRowNotApplicable(
  content: string,
  issue: { area: string; requirement: string },
): string {
  let replaced = false;
  const lines = content.split('\n').map(line => {
    if (replaced) {
      return line;
    }
    const trimmed = line.trim();
    if (!trimmed.startsWith('|') || !trimmed.endsWith('|')) {
      return line;
    }
    const cells = trimmed.split('|').slice(1, -1).map(cell => cell.trim());
    if (
      cells.length < 3 ||
      (cells[0] || 'General') !== issue.area ||
      (cells[1] || 'Unspecified requirement') !== issue.requirement ||
      (cells[2] !== 'Missing' && cells[2] !== 'Inconsistent')
    ) {
      return line;
    }
    replaced = true;
    cells[2] = 'Not applicable';
    return `| ${cells.join(' | ')} |`;
  });
  return lines.join('\n');
}
